import { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ImageUpload from '@/components/ImageUpload';
import { createCustomRequest } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { CheckCircle } from 'lucide-react';

export default function CustomRequestPage() {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', metal_type: 'gold',
    weight: '', description: '', image_url: '', budget: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.description) {
      toast.error('Please fill in all required fields');
      return;
    }
    setLoading(true);
    try {
      const res = await createCustomRequest({
        ...form, weight: form.weight ? parseFloat(form.weight) : null
      });
      toast.success(res.data.message);
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit request');
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-24 text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200, damping: 15 }} className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </motion.div>
          <h2 className="font-display text-2xl font-bold mb-3">Request Received</h2>
          <p className="text-muted-foreground mb-8">Our artisan team will review your design and get in touch within 2-3 business days.</p>
          <Button onClick={() => { setSubmitted(false); setForm({ name:'',email:'',phone:'',metal_type:'gold',weight:'',description:'',image_url:'',budget:'' }); }} variant="outline" className="rounded-full px-8">
            Submit Another
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="custom-request-page">
      <Navbar />
      <div className="grain-overlay" />

      {/* Hero */}
      <section className="relative h-[40vh] md:h-[50vh] overflow-hidden flex items-end">
        <img src="https://images.unsplash.com/photo-1581090465980-58ea88b43443?w=1400&q=80" alt="Craftsman" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 pb-12 w-full">
          <motion.div initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ duration:0.7 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-3">The Atelier</p>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Custom Jewelry</h1>
            <p className="text-muted-foreground mt-3 max-w-md">Upload an image of any jewelry piece and we'll handcraft it for you in pure gold or silver.</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-2xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <motion.form onSubmit={handleSubmit} initial={{ opacity:0,y:20 }} animate={{ opacity:1,y:0 }} transition={{ delay:0.1 }} className="space-y-8">

          {/* Image Upload - Hero */}
          <div data-testid="custom-image-upload">
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3 block">Upload Your Reference Image</label>
            <p className="text-sm text-muted-foreground mb-4">A sketch, photo, or screenshot of the piece you'd like recreated</p>
            <ImageUpload
              value={form.image_url}
              onChange={(url) => setForm({ ...form, image_url: url })}
            />
          </div>

          {/* Description */}
          <div data-testid="custom-description">
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Describe Your Vision *</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Tell us about the design — style, motifs, cultural symbols, engraving text, stone preferences..."
              rows={4}
              className="w-full bg-card border border-border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring/20 transition-shadow"
              required
            />
          </div>

          {/* Metal & Weight */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Metal *</label>
              <Select value={form.metal_type} onValueChange={(v) => setForm({ ...form, metal_type: v })}>
                <SelectTrigger className="rounded-xl bg-card border-border h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="both">Gold + Silver</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Weight (grams)</label>
              <Input type="number" min="1" step="0.5" value={form.weight} onChange={(e) => setForm({ ...form, weight: e.target.value })} placeholder="e.g., 5" className="rounded-xl bg-card border-border h-11" />
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2 block">Budget Range</label>
            <Select value={form.budget} onValueChange={(v) => setForm({ ...form, budget: v })}>
              <SelectTrigger className="rounded-xl bg-card border-border h-11">
                <SelectValue placeholder="Select budget" />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value="under_10k">Under Rs 10,000</SelectItem>
                <SelectItem value="10k_25k">Rs 10,000 - Rs 25,000</SelectItem>
                <SelectItem value="25k_50k">Rs 25,000 - Rs 50,000</SelectItem>
                <SelectItem value="50k_1l">Rs 50,000 - Rs 1,00,000</SelectItem>
                <SelectItem value="above_1l">Above Rs 1,00,000</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Personal Info */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-4">Your Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" className="rounded-xl bg-card border-border h-11" required />
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email *" className="rounded-xl bg-card border-border h-11" required />
              <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" className="rounded-xl bg-card border-border h-11 md:col-span-2" required />
            </div>
          </div>

          <div className="pt-4">
            <Button type="submit" disabled={loading} className="w-full rounded-full h-12 text-sm font-semibold uppercase tracking-wide bg-foreground text-background hover:bg-foreground/90" data-testid="submit-custom-request">
              {loading ? 'Submitting...' : 'Submit Custom Request'}
            </Button>
            <p className="text-xs text-muted-foreground mt-3 text-center">We'll contact you within 2-3 business days</p>
          </div>
        </motion.form>
      </div>

      <Footer />
    </div>
  );
}
